/**
 * QuoteBuilderLauncher · iter303ak — Quote Sent · Task 2
 * ───────────────────────────────────────────────────────
 * Renders one row per primary_quote_type picked at Appraisal Booked.
 * Each row = a "Build [Type]" popout that navigates to the existing
 * Appraisal Builder page in a new window/tab, and a Mark Built checkbox
 * that the operator ticks once the quote is priced.
 *
 * iter303ak-fix14 · Trevor:
 *   • "Option here to add more quotes to the list" — inline dropdown
 *     seeded from /api/dropdowns/preferred_quote_types. Added types
 *     persist to appraisal.primary_quote_types so they survive
 *     refresh + propagate to downstream tasks (Preview / Send).
 *   • "We need to just see the builder tab and the appropriate
 *     builder" — Build button now opens the appraisal in embed=
 *     quote_builder mode, sub-tab pre-selected via QUOTE_TYPE_TO_SUBTAB.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, ExternalLink, Circle, Hammer, AlertCircle, ClipboardCheck, Plus } from 'lucide-react';
import { DRAFT_BUILDERS } from '@/components/DraftBuilderGate';
import QuoteCostCheck from '../components/QuoteCostCheck';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const BUILDER_ROUTE = '/appraisals'; // /:id opens builder page

// v10.25 (Feb 2026 · Go-Live · Bug 4) — Re-Coat quotes now open the
// standalone Painting builder, NOT the combined Restoration builder.
// Trevor's UAT on A999678 / A999680 confirmed the previous "Batch C"
// convergence (Re-Coat aliased to Restoration for UI reuse) was wrong
// for the operator's mental model: pure Re-Coat jobs get their own
// dedicated builder (`PaintingBuilderTab` — standalone paint quote,
// pick a Paint System + roof_m², emit one line per non-optional coat).
// Restoration keeps its own combined Repairs+Paint flow untouched.
// Ordered rules: check re-coat FIRST so it wins over the generic paint
// match; anything else with "restor" or "paint" still lands on
// Restoration (unchanged for the Restoration quote type).
const QUOTE_TYPE_TO_SUBTAB = (ref) => {
  const s = (ref || '').toLowerCase();
  if (/remedial\s*repair/.test(s)) return 'remedial_repairs';
  if (/re[-\s]*coat/.test(s))      return 'painting';       // v10.25 Re-Coat → Painting
  if (/restor|paint/.test(s))      return 'restoration';    // Restoration / mixed paint jobs
  if (/maint/.test(s))             return 'maintenance';
  if (/gutter|spout/.test(s))      return 'guttering';
  if (/repair/.test(s))            return 'repairs';
  if (/re[-\s]*roof|new\s*roof/.test(s)) return 'reroof';
  return 'reroof'; // safe default
};
// Exported for the Jest regression test (see
// __tests__/QuoteBuilderLauncher.routing.test.js). Keeping the mapper
// pure + name-exported means a future refactor can't silently regress
// the quote-type → builder-tab table without breaking a test.
export { QUOTE_TYPE_TO_SUBTAB };

// iter303ak-fix55 · QuotationTypePicker — one-row dropdown that pulls the
// filtered catalogue for (roof_type, category) and PATCHes the child's
// `quotation_type` on pick. Falls back to full catalogue when no filter
// match exists.
// iter303ak-fix58 · Made prominent + moved above the Build button so
// operators can't miss it. `onPicked(ref)` bubbles the pick up so the
// parent can enable the Build button.
function QuotationTypePicker({ childId, rowKey, roofType, category, current, onPicked }) {
  const [items, setItems]   = useState([]);
  const [fallback, setFallback] = useState(false);
  const [value, setValue]   = useState(current || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ roof_type: roofType || '', category: category || '' }).toString();
        const res = await api.get(`/quote-types-catalog?${qs}`);
        if (cancelled) return;
        setItems(res.data?.items || []);
        setFallback(!!res.data?.fallback);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e?.message || 'Failed to load catalogue');
      }
    })();
    return () => { cancelled = true; };
  }, [roofType, category]);

  useEffect(() => { setValue(current || ''); }, [current]);

  const onPick = async (ref) => {
    setValue(ref); setErr(''); setSaving(true);
    try {
      await api.patch(`/appraisals/${childId}`, { quotation_type: ref });
      if (onPicked) onPicked(ref);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const picked = !!value;
  return (
    <div
      className={`mb-3 p-3 rounded-lg border-2 transition ${picked
        ? 'bg-emerald-50 border-emerald-300'
        : 'bg-amber-50 border-amber-400 ring-2 ring-amber-200'}`}
      data-testid={`wf-quotation-type-${rowKey}`}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[11px] font-bold uppercase tracking-widest text-slate-700 shrink-0">
          Step 1 · Quotation Type
        </label>
        {!picked && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-amber-800 bg-white border border-amber-400 rounded px-1.5 py-0.5">
            Pick before building
          </span>
        )}
        <select value={value} onChange={(e) => onPick(e.target.value)}
          disabled={saving || items.length === 0}
          data-testid={`wf-quotation-type-select-${rowKey}`}
          className={`flex-1 min-w-[260px] px-2 py-1.5 text-sm border rounded bg-white ${
            picked ? 'border-emerald-400' : 'border-amber-400 font-semibold'
          }`}>
          <option value="">— pick a quotation type —</option>
          {items.map(r => <option key={r.ref} value={r.ref}>{r.ref}</option>)}
        </select>
        {picked && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5">
            ✓ {value}
          </span>
        )}
        {fallback && items.length > 0 && (
          <span className="text-[10px] text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5" title={`No matches for ${roofType || '—'} + ${category || '—'} — showing full catalogue.`}>Fallback</span>
        )}
        {saving && <span className="text-[10px] text-slate-500">Saving…</span>}
        {err && <span className="text-[10px] text-rose-700">{err}</span>}
      </div>
      <div className="text-[10px] text-slate-500 mt-1">
        Drives the description + details on the quotation PDF · filtered by roof <b>{roofType || '—'}</b> · category <b>{category || '—'}</b>
      </div>
    </div>
  );
}


// iter303ak-fix58 · RowTotalBadge — fetches this child's quote summary
// once (with a refresh trigger prop that bumps when the builder closes)
// and displays the grand-total-inc-GST inline in the row header.
function RowTotalBadge({ childId, rowKey, refreshKey }) {
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!childId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/quote-summary/${childId}`);
        if (cancelled) return;
        // v10.18 (Feb 2026 · UAT A999585) · Prefer the builder's own
        // authoritative grand total when it's set. `grand_total_inc_gst`
        // is derived from `quote_items` which is empty/stale until the
        // operator pushes the reroof spec to Components — showing $775
        // (raw diagram pins) instead of the $30,185 the builder already
        // knows. `builder_grand_total_inc_gst` = `appraisal.calc_grand_total`
        // × 1.15 = the same number the reroof builder's green Grand-total
        // panel shows. Fallback to the quote_items rollup for non-reroof
        // flows (Maintenance / Painting where calc_grand_total is 0).
        const builder = Number(data?.builder_grand_total_inc_gst || 0);
        const items   = Number(data?.grand_total_inc_gst || 0);
        setTotal(builder > 0 ? builder : items);
      } catch {
        if (!cancelled) setTotal(null);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [childId, refreshKey]);

  if (loading) {
    return (
      <span
        className="text-[10px] font-medium tracking-wide text-slate-500 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5"
        data-testid={`wf-quote-row-total-${rowKey}-loading`}
      >…</span>
    );
  }
  if (total == null || total <= 0) {
    // v10.24 (Feb 2026 · Go-Live prep · Bug 2 Answer A) — At go-live,
    // fresh children arrive at $0 (no auto-emit of pins → priced
    // component_lines). Instead of showing a bland "$0.00" that reads
    // like a real amount, tell the operator explicitly what to do next.
    return (
      <span
        className="text-[10px] font-semibold tracking-wide text-amber-800 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5"
        data-testid={`wf-quote-row-total-${rowKey}-empty`}
        title="Open the builder below to price this quote — pins carry measurements only."
      >Not yet priced — open builder to start</span>
    );
  }
  return (
    <span
      className="text-[10px] font-bold tracking-wide text-amber-900 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5"
      data-testid={`wf-quote-row-total-${rowKey}`}
      title="Grand total incl GST — refreshes when the builder closes."
    >
      ${total.toLocaleString('en-NZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} incl GST
    </span>
  );
}


export default function QuoteBuilderLauncher({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;
  const bookedTypes = capturedData?.appraisal?.primary_quote_types
    || capturedData?.appraisal_record?.primary_quote_types
    || capturedData?.primary_quote_types
    || [];

  // iter303ak-fix14 · Local + persisted list. `bookedTypes` seeds the
  // component; the operator can add more via the "+ Add another quote
  // type" dropdown below, which PATCHes appraisal.primary_quote_types
  // so refresh / downstream tasks see the addition.
  const [primaryTypes, setPrimaryTypes] = useState(bookedTypes);
  useEffect(() => { setPrimaryTypes(bookedTypes); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [JSON.stringify(bookedTypes)]);

  const initial = value || capturedData?.appraisal?.quote_build_status || {};
  const [status, setStatus] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // iter303ak-fix55 · Property roof_type used to filter the quotation-type
  // catalogue per child row.
  const roofType = capturedData?.roof_record?.roof_type
    || capturedData?.property_record?.roof_details?.[0]?.roof_type
    || capturedData?.property?.roof_details?.[0]?.roof_type
    || '';

  // iter303ak-fix21 · Parent-Child spawn on mount. Task 2 Action 1 is
  // the boundary where each quote type + variant materialises as its
  // own child appraisal (with own appraisal_no, own components,
  // materials, contractors, pipeline_progress). This POST is idempotent
  // — safe to call every mount; server only creates missing children.
  //
  // v10.22 (Feb 2026 · UAT A999617 quote-duplication) — Guard against
  // overlapping concurrent POSTs. The launcher remounts on every
  // `runnerReloadKey` bump (mid-status hop, refresh + resume, StrictMode
  // double-invoke in dev), and without a re-entry guard two POSTs would
  // race the backend's read-then-write dedupe. `spawnInFlightRef` is a
  // useRef so it survives across the same-tick double-invoke that
  // useState would miss; the backend also holds a per-parent asyncio
  // lock + a Mongo unique index as belt-and-braces.
  const [children, setChildren] = useState([]);
  const [spawning, setSpawning] = useState(false);
  const spawnInFlightRef = useRef(false);
  const spawnAndLoad = useCallback(async () => {
    if (!appraisalId) return;
    if (spawnInFlightRef.current) return;   // v10.22 re-entry guard
    spawnInFlightRef.current = true;
    setSpawning(true);
    try {
      const { data } = await api.post(`/appraisals/${appraisalId}/spawn-children`);
      setChildren(Array.isArray(data?.children) ? data.children : []);
    } catch (e) {
      // If this appraisal is already a child, the endpoint 400s — just
      // fetch our siblings via the /children endpoint.
      const detail = e?.response?.data?.detail || '';
      if (/already a child/i.test(detail)) {
        try {
          const { data } = await api.get(`/appraisals/${appraisalId}/children`);
          setChildren(Array.isArray(data?.children) ? data.children : []);
        } catch { setChildren([]); }
      } else {
        setErr(detail || e?.message || 'Failed to load quote children');
      }
    } finally {
      setSpawning(false);
      spawnInFlightRef.current = false;
    }
  }, [appraisalId]);
  useEffect(() => { spawnAndLoad(); }, [spawnAndLoad]);

  // Catalog for the "+ Add another quote type" dropdown — pulled once.
  const [catalog, setCatalog] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/dropdowns/preferred_quote_types');
        if (!cancelled) setCatalog(Array.isArray(data) ? data : []);
      } catch { if (!cancelled) setCatalog([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  // iter303ak-fix17 · Auto-surface variants — Trevor: "these are the
  // options already created and should be showing on the list ... if I
  // add the extra variant in the builder, it needs to be represented".
  //   • Every variant returned by /quote-variants renders as a sub-row
  //     automatically on mount (no opt-in click required).
  //   • `skippedVariantIds` captures the negative list — variant_ids
  //     the operator explicitly removed via the row's [×] Remove.
  //   • "N variant(s) hidden — show" bulk toggle un-skips them.
  //   • "+ Create new variant in builder" opens the builder embed at
  //     the parent type's subtab (no variant deep-link). We poll the
  //     popup for close and refetch variants, so a newly-cloned
  //     variant appears in the launcher without a page refresh.
  const [availableVariants, setAvailableVariants] = useState([]);
  const [skippedVariantIds, setSkippedVariantIds] = useState(
    Array.isArray(capturedData?.appraisal?.quote_variants_skipped)
      ? capturedData.appraisal.quote_variants_skipped
      : []
  );
  const [showSkippedFor, setShowSkippedFor] = useState({});  // parent_type -> bool
  const [creatingVariantFor, setCreatingVariantFor] = useState(null);   // parent_type
  const variantPollerRef = useRef(null);
  const fetchVariants = useCallback(async () => {
    if (!appraisalId) return;
    try {
      const { data } = await api.get(`/appraisals/${appraisalId}/quote-variants`);
      setAvailableVariants(Array.isArray(data?.variants) ? data.variants : []);
    } catch { setAvailableVariants([]); }
  }, [appraisalId]);
  useEffect(() => { fetchVariants(); }, [fetchVariants]);
  useEffect(() => () => {
    if (variantPollerRef.current) clearInterval(variantPollerRef.current);
  }, []);

  const variantKey = (parentType, variantId) => `${parentType}::${variantId}`;

  // iter303ak-fix61 · Refresh trigger for RowTotalBadge.
  // Bumped whenever the child's builder pop-out closes OR the runner
  // tab regains focus, so the amber total pill re-fetches
  // /quote-summary/{child_id} without needing the operator to
  // manually tick the row.
  const [builderCloseTick, setBuilderCloseTick] = useState(0);
  const bumpBuilderCloseTick = useCallback(() => setBuilderCloseTick(t => t + 1), []);
  // Safety net: re-tick when the runner tab regains visibility.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        bumpBuilderCloseTick();
        // Also refresh the child list so quotation_type / status
        // changes made inside the builder land in the row.
        spawnAndLoad();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [bumpBuilderCloseTick, spawnAndLoad]);

  // Reference for the "Build in Appraisal Builder" close poller. Same
  // pattern as `variantPollerRef` but for regular per-row builder pops.
  const builderPollersRef = useRef({});
  useEffect(() => () => {
    Object.values(builderPollersRef.current).forEach(id => clearInterval(id));
    builderPollersRef.current = {};
  }, []);

  const removeVariantRow = async (parentType, variantId) => {
    // Add to negative-list so this variant stays hidden across refresh.
    const next = skippedVariantIds.includes(variantId)
      ? skippedVariantIds
      : [...skippedVariantIds, variantId];
    setSkippedVariantIds(next);
    // Clear tracking state so the removed variant doesn't block the gate.
    const k = variantKey(parentType, variantId);
    setStatus(prev => { const { [k]: _drop, ...rest } = prev; return rest; });
    setReviewStatus(prev => { const { [k]: _drop, ...rest } = prev; return rest; });
    if (appraisalId) {
      try { await api.patch(`/appraisals/${appraisalId}`, { quote_variants_skipped: next }); }
      catch { /* non-blocking */ }
    }
  };

  const restoreVariantRow = async (variantId) => {
    const next = skippedVariantIds.filter(v => v !== variantId);
    setSkippedVariantIds(next);
    if (appraisalId) {
      try { await api.patch(`/appraisals/${appraisalId}`, { quote_variants_skipped: next }); }
      catch { /* non-blocking */ }
    }
  };

  const createVariantInBuilder = async (parentType) => {
    if (!appraisalId) return;
    const subtab = QUOTE_TYPE_TO_SUBTAB(parentType);
    // Stage 2 · new variants are built on a fresh draft child, never on the parent.
    // Open the window synchronously (popup blockers) and point it at the draft once created.
    const win = window.open('about:blank', '_blank');
    if (!win) { setErr('Popup blocked — check your browser'); return; }
    let targetId = appraisalId;
    if (DRAFT_BUILDERS.includes(subtab)) {
      try {
        const { data } = await api.post(`/appraisals/${appraisalId}/builder-draft`, { builder: subtab, new: true });
        targetId = data?.draft?.id || appraisalId;
      } catch (e) { win.close(); setErr(e?.response?.data?.detail || 'Could not create draft'); return; }
    }
    win.location.href = `${BUILDER_ROUTE}/${targetId}?embed=quote_builder&builder=${encodeURIComponent(subtab)}`;
    setCreatingVariantFor(parentType);
    // Poll for close, then refetch variants so newly-cloned ones appear
    // in the launcher without a page refresh (Trevor's b=yes).
    if (variantPollerRef.current) clearInterval(variantPollerRef.current);
    variantPollerRef.current = setInterval(() => {
      if (win.closed) {
        clearInterval(variantPollerRef.current);
        variantPollerRef.current = null;
        setCreatingVariantFor(null);
        fetchVariants();
        // iter303ak-fix21 · Any new variant created in the builder
        // spawns a fresh child appraisal — refresh the children list
        // so the new appraisal_no badge appears immediately.
        spawnAndLoad();
      }
    }, 500);
  };

  // iter303ak-fix22 · Manual refresh fallback when the popup poller
  // misses the close (e.g. operator navigated the popup away instead
  // of closing it). Re-pulls variants + children in one click.
  const [refreshing, setRefreshing] = useState(false);
  const manualRefresh = async () => {
    setRefreshing(true);
    try { await Promise.all([fetchVariants(), spawnAndLoad()]); }
    finally { setRefreshing(false); }
  };

  useEffect(() => {
    if (onChange) onChange(status);
  }, [status, onChange]);

  // iter303ak-fix15 · Per-quote-type review state. `reviewStatus` tracks
  // reviewed/at per quote_type_ref; `reviewNotes` is the free-text
  // operator commentary. Both persist to appraisal.quote_review_* on
  // Save & Continue (below). Expanded panels tracked in `openReview`.
  const [reviewStatus, setReviewStatus] = useState(
    value?.review_status
    || capturedData?.appraisal?.quote_review_status
    || {}
  );
  const [reviewNotes, setReviewNotes] = useState(
    capturedData?.appraisal?.quote_review_notes || {}
  );
  const [openReview, setOpenReview] = useState({});
  const [costOk, setCostOk] = useState({});
  const setCostOkFor = useCallback((k, ok) => setCostOk(prev => (prev[k] === ok ? prev : { ...prev, [k]: ok })), []);

  const setNote = (rowKey, text) => setReviewNotes(prev => ({ ...prev, [rowKey]: text }));

  const approve = (rowKey) => {
    setReviewStatus(prev => ({
      ...prev,
      [rowKey]: { reviewed: true, at: new Date().toISOString() },
    }));
    // iter303ak-fix22 · Trevor: "can the review window be minimised
    // instead of staying open". Auto-collapse the panel after lock so
    // the row list stays compact.
    setOpenReview(prev => ({ ...prev, [rowKey]: false }));
  };

  const adjustInBuilder = (rowKey, parentRef, variantId) => {
    // Un-tick both Built + Reviewed so the operator cycles through the
    // whole loop again after edits — matches Trevor's "cycle until
    // correct" flow. Then re-open the builder embed popout.
    setStatus(prev => ({ ...prev, [rowKey]: { built: false, at: null } }));
    setReviewStatus(prev => ({ ...prev, [rowKey]: { reviewed: false, at: null } }));
    openBuilder(parentRef, variantId);
  };

  // iter303ak-fix22 · Trevor: Preview Report PDF was downloading
  // instead of opening in a new tab. Backend now honours ?inline=1 by
  // switching Content-Disposition from `attachment` to `inline`.
  const previewHref = appraisalId
    ? `${process.env.REACT_APP_BACKEND_URL || ''}/api/reports/quotation-detailed/${appraisalId}?inline=1`
    : '#';

  const toggle = (rowKey) => {
    const cur = status[rowKey];
    const next = {
      ...status,
      [rowKey]: cur?.built
        ? { built: false, at: null }
        : { built: true, at: new Date().toISOString() },
    };
    setStatus(next);
  };

  const openBuilder = (ref, variantId, childId) => {
    if (!appraisalId) return;
    // iter303ak-fix63 · Clear any stale error before opening (was
    // sticking the false-positive "Popup blocked" message from a
    // previous open where noopener caused window.open to return null).
    setErr('');
    const subtab = QUOTE_TYPE_TO_SUBTAB(ref);
    // iter303ak-fix65 · F2 Tier 1 (reviewer-informed) — PATH SWAP the
    // child id in when we have one. Previously the URL always used the
    // parent appraisal id, so every builder push landed on the parent
    // and phantom sibling variants spawned back on the runner (C2678).
    // Path swap (not query param) so downstream code inside
    // AppraisalDetailPage reads the child id via `useParams()` without
    // any additional plumbing.
    const targetId = childId || appraisalId;
    let url = `${BUILDER_ROUTE}/${targetId}?embed=quote_builder&builder=${encodeURIComponent(subtab)}`;
    // iter303ak-fix16 · deep-link to specific variant when provided
    if (variantId) url += `&variant=${encodeURIComponent(variantId)}`;
    // iter303ak-fix63 · Dropped `noopener,noreferrer` — with noopener,
    // Chrome returns null from window.open even on success, which was
    // false-triggering the popup-blocked warning. Same-origin, safe
    // to keep the handle (we need it for the close poller anyway).
    const win = window.open(url, '_blank');
    if (!win) { setErr('Popup blocked — check your browser'); return; }
    // iter303ak-fix61 · Poll for close so the row's inline total pill
    // refreshes as soon as the operator closes the builder tab (Trevor:
    // "closed window and $ value has not updated"). Uses a per-row key
    // so multiple builders can be open simultaneously without stepping
    // on each other.
    const pollKey = variantId ? variantKey(ref, variantId) : ref;
    if (builderPollersRef.current[pollKey]) clearInterval(builderPollersRef.current[pollKey]);
    builderPollersRef.current[pollKey] = setInterval(() => {
      if (win.closed) {
        clearInterval(builderPollersRef.current[pollKey]);
        delete builderPollersRef.current[pollKey];
        // Auto-push the build into this quote's components on close (no manual Push in runner builders).
        const push = targetId !== appraisalId
          ? api.post(`/appraisals/${targetId}/components/pull-from-child-builder`)
            .then(({ data }) => {
              if (data?.skipped || data?.error) toast.error(`Quote not pushed to components: ${data.error || 'nothing built yet'}`);
              else {
                toast.success(`Quote updated · ${data?.inserted ?? 0} line(s) pushed to components`);
                if ((data?.inserted ?? 0) > 0) setStatus(prev => ({ ...prev, [pollKey]: { built: true, at: new Date().toISOString() } }));
              }
            })
            .catch((e) => toast.error(e?.response?.data?.detail || 'Auto-push to components failed'))
          : Promise.resolve();
        push.finally(() => {
          bumpBuilderCloseTick();
          spawnAndLoad();
          fetchVariants();
        });
      }
    }, 750);
  };

  // iter303ak-fix14 · Add another quote type on the fly. Persists to
  // appraisal.primary_quote_types immediately so refresh / downstream
  // tasks (Preview PDFs, Send) see the addition.
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const availableToAdd = catalog.filter(t => !primaryTypes.includes(t));
  const addQuoteType = async (ref) => {
    if (!ref || primaryTypes.includes(ref) || !appraisalId) return;
    setAdding(true); setErr('');
    try {
      const next = [...primaryTypes, ref];
      await api.patch(`/appraisals/${appraisalId}`, { primary_quote_types: next });
      setPrimaryTypes(next);
      setAddPickerOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to add quote type');
    } finally {
      setAdding(false);
    }
  };

  // iter303ak-fix17 · Auto-render variants — every discovered variant
  // becomes a launcher sub-row unless the operator explicitly skipped
  // it. Row key = `${parent_type}::${variant_id}`.
  // iter303ak-fix63 · Trevor C2666: an orphan Restoration variant
  // (from an older test run) was landing in `visibleVariants` but
  // never rendering because Restoration isn't in `primary_quote_types`
  // → counter showed "2/3 built" with only 2 rows on screen → Continue
  // stuck forever. Split into visibleVariants (all) and
  // renderableVariants (parent_type in primaries) so orphans no longer
  // affect the counter, and surface a small info banner so Trevor
  // knows they exist.
  const visibleVariants = availableVariants.filter(v => !skippedVariantIds.includes(v.variant_id));
  const primarySet = new Set(primaryTypes);
  const renderableVariants = visibleVariants.filter(v =>
    primarySet.has(v.parent_type) || primarySet.has(v.quote_type_ref)
  );
  const orphanVariants = visibleVariants.filter(v =>
    !primarySet.has(v.parent_type) && !primarySet.has(v.quote_type_ref)
  );
  const allRowKeys = [
    ...primaryTypes.map(t => ({ parentType: t, variantId: null, key: t })),
    ...renderableVariants.map(v => ({
      parentType: v.parent_type,
      variantId: v.variant_id,
      key: variantKey(v.parent_type, v.variant_id),
    })),
  ];
  const allBuilt = allRowKeys.length === 0
    || allRowKeys.every(r => status[r.key]?.built === true);
  const allReviewed = allRowKeys.length === 0
    || allRowKeys.every(r => reviewStatus[r.key]?.reviewed === true);
  const canContinue = allBuilt && allReviewed;

  const advance = async () => {
    setSaving(true); setErr('');
    try {
      if (appraisalId) {
        await api.patch(`/appraisals/${appraisalId}`, {
          quote_build_status: status,
          quote_review_status: reviewStatus,
          quote_review_notes: reviewNotes,
          quote_variants_skipped: skippedVariantIds,
        });
      }
      onNext();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-quote-builder-launcher">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <Hammer className="w-5 h-5 text-indigo-700 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Build Quote(s)</div>
            <div className="text-sm text-indigo-800 mt-1">{params.prompt || 'Build each quote type — click "Build" to launch the Appraisal Builder, price it, then tick "Built" to mark it complete.'}</div>
          </div>
          {/* iter303ak-fix22 · Manual refresh — safety net when the
              popup close-poller misses (operator kept popup open, or
              navigated away). One-click re-pulls variants + children. */}
          <button
            onClick={manualRefresh}
            disabled={refreshing || !appraisalId}
            className="shrink-0 px-3 py-1.5 rounded-md text-xs bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40"
            data-testid="wf-quote-manual-refresh"
            title="Re-scan the builder for new variants and refresh the child appraisal list"
          >
            {refreshing ? 'Refreshing…' : '↻ Refresh variants'}
          </button>
        </div>
      </div>

      {/* iter303ak-fix63 · Orphan variants banner — surfaces any child
          appraisal whose `parent_type` (e.g. Restoration) isn't in the
          currently-picked `primary_quote_types`. Rows for these don't
          render (they'd confuse the flow) but Trevor still needs to
          know they exist so he can either delete them or add the
          missing primary type upstream. */}
      {orphanVariants.length > 0 && (
        <div
          className="flex items-start gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3"
          data-testid="wf-quote-builder-orphans"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
          <div className="flex-1">
            <div className="font-medium text-slate-800">
              {orphanVariants.length} orphan variant{orphanVariants.length === 1 ? '' : 's'} hidden
            </div>
            <div className="text-slate-600 mt-0.5">
              {orphanVariants.map(v => `${v.parent_type} · ${v.variant_label || v.variant_id}`).join(', ')}
              {' — '}not counted toward the "built / reviewed" tally. Add the type to <b>primary_quote_types</b> upstream (Appraisal Booked → Action 6) or delete the child appraisal via the Admin panel to clear this.
            </div>
          </div>
        </div>
      )}

      {primaryTypes.length === 0 ? (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="wf-quote-builder-empty">
          <AlertCircle className="w-4 h-4 mt-0.5" />
          <div>
            <div className="font-medium">No primary quote types found on this appraisal.</div>
            <div className="text-xs mt-1">Add one below, or go back to Appraisal Booked · Schedule Appraisal · Action 6 to pick the primary trade(s).</div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {primaryTypes.map((ref) => {
            // iter303ak-fix17 · Auto-surface: every discovered variant
            // for this parent auto-renders as a sub-row UNLESS in the
            // skipped list. "N hidden — show" toggle re-exposes them.
            const myVariants = visibleVariants.filter(v => v.parent_type === ref || v.quote_type_ref === ref);
            const mySkippedCount = availableVariants.filter(v =>
              (v.parent_type === ref || v.quote_type_ref === ref)
              && skippedVariantIds.includes(v.variant_id)
            ).length;
            const showSkipped = !!showSkippedFor[ref];
            const mySkippedVariants = showSkipped
              ? availableVariants.filter(v =>
                  (v.parent_type === ref || v.quote_type_ref === ref)
                  && skippedVariantIds.includes(v.variant_id)
                )
              : [];
            const isCreating = creatingVariantFor === ref;
            // iter303ak-fix21 · Look up the child appraisal_no for this
            // row so the operator sees the new sequential number
            // (A5155, A5156, …) alongside each quote/variant. Every
            // spawned child renders its badge; unspawned rows show a
            // "spawning…" placeholder briefly.
            const baseOf = (c) => (c.primary_quote_types && c.primary_quote_types[0]) || c.quote_type_ref;
            const childForParent = children.find(c => baseOf(c) === ref && !c.variant_label);
            const childrenForVariants = myVariants.map(v =>
              children.find(c => baseOf(c) === ref
                && (c.variant_metadata?.variant_id === v.variant_id
                    || c.variant_label === v.variant_label))
            );

            const renderRow = ({ rowKey, parentRef, variantId, label, sublabel, indented, childAppraisalNo, childRecord }) => {
              const built = status[rowKey]?.built === true;
              const reviewed = reviewStatus[rowKey]?.reviewed === true;
              const isOpen = !!openReview[rowKey];
              const note = reviewNotes[rowKey] || '';
              const ringClass = reviewed
                ? 'bg-emerald-50 border-emerald-400'
                : built ? 'bg-sky-50 border-sky-300' : 'bg-white border-slate-200';
              return (
                <div
                  key={rowKey}
                  className={`p-3 rounded-lg border transition ${ringClass} ${indented ? 'ml-6 border-l-4 border-l-indigo-300' : ''}`}
                  data-testid={`wf-quote-row-${rowKey}`}
                >
                  {/* iter303ak-fix58 · Quotation Type picker MOVED TO TOP
                      of the row so operators can't miss it. Row now
                      reads top-down: (1) pick quotation type,
                      (2) build in the builder, (3) review. Build
                      button below is disabled until Step 1 is done.
                      iter303ak-fix60 · Guard now accepts BOTH `.id`
                      and `._id` — the `/children` endpoint returns
                      `id` (serialized) not `_id`, which caused the
                      picker to never render on C2665. */}
                  {(childRecord?._id || childRecord?.id) && (
                    <QuotationTypePicker
                      childId={childRecord._id || childRecord.id}
                      rowKey={rowKey}
                      roofType={roofType}
                      category={parentRef}
                      current={childRecord.quotation_type || ''}
                      onPicked={(ref) => setChildren(prev => prev.map(c => (
                        ((c.id && c.id === (childRecord.id || childRecord._id))
                          || (c._id && c._id === (childRecord._id || childRecord.id)))
                          ? { ...c, quotation_type: ref }
                          : c
                      )))}
                    />
                  )}

                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => toggle(rowKey)}
                      className="shrink-0"
                      aria-label={built ? `Mark ${label} not built` : `Mark ${label} built`}
                      data-testid={`wf-quote-tick-${rowKey}`}
                    >
                      {built
                        ? <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                        : <Circle className="w-6 h-6 text-slate-300 hover:text-slate-500" />}
                    </button>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-slate-800 flex items-center gap-2 flex-wrap">
                        {label}
                        {childAppraisalNo && (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5" data-testid={`wf-quote-child-no-${rowKey}`}>
                            A{childAppraisalNo}
                          </span>
                        )}
                        {/* iter303ak-fix58 · Inline grand-total badge —
                            refreshes when this row's builder closes.
                            iter303ak-fix60 · Accept both `.id` and
                            `._id` (children endpoint returns `.id`).
                            iter303ak-fix61 · refreshKey now combines
                            tick moments AND builderCloseTick so the
                            pill updates as soon as the builder tab
                            is closed (no manual tick needed). */}
                        {(childRecord?._id || childRecord?.id) && (
                          <RowTotalBadge
                            childId={childRecord._id || childRecord.id}
                            rowKey={rowKey}
                            refreshKey={`${status[rowKey]?.at || 0}::${builderCloseTick}`}
                          />
                        )}
                        {reviewed && (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-300 rounded px-1.5 py-0.5">
                            ✓ Reviewed
                          </span>
                        )}
                      </div>
                      {sublabel && (
                        <div className="text-xs text-indigo-700 font-medium mt-0.5">{sublabel}</div>
                      )}
                      <div className="text-xs mt-0.5">
                        {reviewed && reviewStatus[rowKey]?.at ? (
                          <span className="text-emerald-700">
                            Reviewed &amp; locked · {new Date(reviewStatus[rowKey].at).toLocaleString()}
                          </span>
                        ) : built && status[rowKey]?.at ? (
                          <span className="text-sky-700">
                            Built · {new Date(status[rowKey].at).toLocaleString()} · <span className="font-semibold">awaiting review</span>
                          </span>
                        ) : (
                          <span className="text-slate-500">Not built yet.</span>
                        )}
                      </div>
                    </div>
                    {built && (
                      <button
                        onClick={() => setOpenReview(prev => ({ ...prev, [rowKey]: !prev[rowKey] }))}
                        className={`px-3 py-1.5 rounded-md text-xs inline-flex items-center gap-1 border ${isOpen ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'}`}
                        data-testid={`wf-quote-review-toggle-${rowKey}`}
                      >
                        <ClipboardCheck className="w-3.5 h-3.5" /> Review {isOpen ? '▲' : '▼'}
                      </button>
                    )}
                    <button
                      onClick={() => openBuilder(parentRef, variantId, childRecord?.id || childRecord?._id)}
                      disabled={!appraisalId || !childRecord?.quotation_type}
                      title={!childRecord?.quotation_type ? 'Pick a Quotation Type above first' : undefined}
                      className="px-3 py-1.5 rounded-md text-xs bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1"
                      data-testid={`wf-quote-open-builder-${rowKey}`}
                    >
                      <ExternalLink className="w-3.5 h-3.5" /> Build in Appraisal Builder
                    </button>
                    {variantId && (
                      <button
                        onClick={() => removeVariantRow(parentRef, variantId)}
                        className="px-2 py-1.5 rounded-md text-xs text-slate-400 hover:text-rose-600 hover:bg-rose-50"
                        title="Remove this variant from the quote package"
                        data-testid={`wf-quote-remove-variant-${rowKey}`}
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {/* iter303ak-fix58 · QuotationTypePicker moved to the
                      TOP of the row (above the tick / build button
                      strip). Duplicate placement removed to keep the
                      row clean. */}

                  {/* Review panel */}
                  {built && isOpen && (
                    <div className="mt-3 pt-3 border-t border-slate-200 space-y-3" data-testid={`wf-quote-review-panel-${rowKey}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 flex items-center gap-1.5">
                          <ClipboardCheck className="w-3.5 h-3.5 text-emerald-700" />
                          Quick Quote Review — {label}{sublabel ? ` · ${sublabel}` : ''}
                        </div>
                        <a
                          href={(childRecord?._id || childRecord?.id) ? `${process.env.REACT_APP_BACKEND_URL || ''}/api/reports/quotation-detailed/${childRecord._id || childRecord.id}?inline=1` : previewHref}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 rounded-md text-xs bg-slate-700 hover:bg-slate-800 text-white inline-flex items-center gap-1"
                          data-testid={`wf-quote-preview-pdf-${rowKey}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Preview Quotation Letter PDF
                        </a>
                      </div>

                      <QuoteCostCheck api={api} childId={childRecord?._id || childRecord?.id} rowKey={rowKey}
                        refreshKey={`${status[rowKey]?.at || 0}::${builderCloseTick}`} onOkChange={setCostOkFor} />

                      <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">
                          Review notes / adjustments needed
                        </label>
                        <textarea
                          value={note}
                          onChange={(e) => setNote(rowKey, e.target.value)}
                          rows={2}
                          placeholder="Anything the client should know, or adjustments you want to make before locking this quote…"
                          className="w-full text-sm border border-slate-300 rounded p-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 outline-none"
                          data-testid={`wf-quote-review-notes-${rowKey}`}
                        />
                      </div>

                      <div className="flex items-center gap-2 justify-end">
                        <button
                          onClick={() => adjustInBuilder(rowKey, parentRef, variantId)}
                          className="px-3 py-1.5 rounded-md text-xs bg-white border border-orange-300 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1"
                          data-testid={`wf-quote-adjust-${rowKey}`}
                          title="Un-tick built + reviewed and re-open the builder — cycles the loop"
                        >
                          <Hammer className="w-3.5 h-3.5" /> Adjust in Builder
                        </button>
                        {!reviewed ? (
                          <button
                            onClick={() => approve(rowKey)}
                            disabled={!costOk[rowKey]}
                            title={!costOk[rowKey] ? 'Check the cost warning above first' : undefined}
                            className="px-3 py-1.5 rounded-md text-xs bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid={`wf-quote-approve-${rowKey}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve &amp; lock
                          </button>
                        ) : (
                          <button
                            onClick={() => setReviewStatus(prev => ({ ...prev, [rowKey]: { reviewed: false, at: null } }))}
                            className="px-3 py-1.5 rounded-md text-xs bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 inline-flex items-center gap-1"
                            data-testid={`wf-quote-unlock-${rowKey}`}
                            title="Un-approve so you can make edits"
                          >
                            Un-lock review
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            };

            return (
              <React.Fragment key={ref}>
                {/* Parent row */}
                {renderRow({
                  rowKey: ref,
                  parentRef: ref,
                  variantId: null,
                  label: ref,
                  sublabel: null,
                  indented: false,
                  childAppraisalNo: childForParent?.appraisal_no,
                  childRecord: childForParent,
                })}
                {/* Variant sub-rows */}
                {myVariants.map((v, idx) => renderRow({
                  rowKey: variantKey(ref, v.variant_id),
                  parentRef: ref,
                  variantId: v.variant_id,
                  label: ref,
                  sublabel: v.variant_label,
                  indented: true,
                  childAppraisalNo: childrenForVariants[idx]?.appraisal_no,
                  childRecord: childrenForVariants[idx],
                }))}
                {/* Skipped variant rows (only when operator clicks "show") */}
                {mySkippedVariants.map(v => (
                  <div
                    key={`skipped-${v.variant_id}`}
                    className="ml-6 border-l-4 border-l-slate-200 p-2 rounded-r-md bg-slate-100/60 flex items-center gap-3 text-slate-500 text-sm"
                    data-testid={`wf-quote-skipped-row-${ref}-${v.variant_id}`}
                  >
                    <span className="line-through">{ref} · {v.variant_label}</span>
                    <span className="text-[10px] uppercase tracking-widest font-semibold bg-slate-200 px-1.5 py-0.5 rounded">Hidden</span>
                    <div className="flex-1" />
                    <button
                      onClick={() => restoreVariantRow(v.variant_id)}
                      className="px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 border border-emerald-300 rounded"
                      data-testid={`wf-quote-restore-variant-${ref}-${v.variant_id}`}
                    >
                      Restore
                    </button>
                  </div>
                ))}
                {/* Action strip under each parent: create new variant + show/hide skipped */}
                <div className="ml-6 flex items-center gap-3 pt-1" data-testid={`wf-variant-actions-${ref}`}>
                  <button
                    onClick={() => createVariantInBuilder(ref)}
                    disabled={!appraisalId || isCreating}
                    className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium text-indigo-700 border border-dashed border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 rounded-md disabled:opacity-50"
                    data-testid={`wf-create-variant-${ref}`}
                    title={`Open the ${ref} builder to Clone as Variant — the new variant will appear here after you close the builder`}
                  >
                    <Plus className="w-3 h-3" />
                    {isCreating ? `Waiting for ${ref} builder to close…` : `Create new variant in ${ref} builder`}
                  </button>
                  {mySkippedCount > 0 && (
                    <button
                      onClick={() => setShowSkippedFor(prev => ({ ...prev, [ref]: !prev[ref] }))}
                      className="text-xs text-slate-500 hover:text-slate-800 underline decoration-dotted"
                      data-testid={`wf-toggle-skipped-${ref}`}
                    >
                      {showSkipped
                        ? 'Hide skipped variants'
                        : `${mySkippedCount} variant${mySkippedCount === 1 ? '' : 's'} hidden — show`}
                    </button>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* iter303ak-fix14 · Add another quote type — persists to
          appraisal.primary_quote_types so refresh survives. */}
      <div className="pt-1" data-testid="wf-add-quote-type-wrapper">
        {!addPickerOpen ? (
          <button
            onClick={() => setAddPickerOpen(true)}
            disabled={!appraisalId || availableToAdd.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-700 border border-dashed border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 rounded-md disabled:opacity-40"
            data-testid="wf-add-quote-type-open"
            title={availableToAdd.length === 0 ? 'Every catalog quote type is already added' : 'Add another quote type to this appraisal'}
          >
            <Plus className="w-3.5 h-3.5" /> Add another quote type
          </button>
        ) : (
          <div className="flex items-center gap-2 p-2 bg-white border border-indigo-200 rounded-md">
            <select
              className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded"
              data-testid="wf-add-quote-type-picker"
              defaultValue=""
              onChange={(e) => { if (e.target.value) addQuoteType(e.target.value); }}
              disabled={adding}
            >
              <option value="">Pick a quote type…</option>
              {availableToAdd.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button
              onClick={() => setAddPickerOpen(false)}
              className="px-2 py-1 text-xs text-slate-500 hover:text-slate-800"
              data-testid="wf-add-quote-type-cancel"
              disabled={adding}
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <div className="text-xs text-slate-500 flex items-center gap-1">
        <ClipboardCheck className="w-3.5 h-3.5" />
        {canContinue
          ? `All ${allRowKeys.length} quote${allRowKeys.length === 1 ? '' : 's'} built & reviewed. Continue when ready.`
          : `${allRowKeys.filter(r => status[r.key]?.built).length}/${allRowKeys.length} built · ${allRowKeys.filter(r => reviewStatus[r.key]?.reviewed).length}/${allRowKeys.length} reviewed`}
      </div>

      {err ? (
        <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-md p-3">
          <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{err}</span>
        </div>
      ) : null}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-qbl-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel ? (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-qbl-cancel">Cancel</button>
          ) : null}
          <button
            onClick={advance}
            disabled={saving || !canContinue}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-qbl-continue"
          >
            <CheckCircle2 className="w-4 h-4" /> {saving ? 'Saving…' : 'Save & Continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
